import React, { useState, useRef } from "react";
import { adminApi } from "../../api";
import { useContent } from "../../context/ContentContext";
import ImageUpload from "../../components/admin/ImageUpload";
import { IMAGE_SPECS, SECTION_IMAGE_SLOTS, IMAGE_GENERAL_TIP } from "../../constants/imageSpecs";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import { Save, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

const TABS = [
  { key: "company", label: "Company" },
  { key: "hero", label: "Hero & Images" },
  { key: "stats", label: "Stats" },
  { key: "services", label: "Services" },
  { key: "whyChoose", label: "Why Choose" },
  { key: "processSteps", label: "Process" },
  { key: "certifications", label: "Certifications" },
  { key: "regions", label: "Regions" },
  { key: "testimonials", label: "Testimonials" },
];

function Field({ label, value, onChange, textarea, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {textarea ? (
        <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}

// Wait for saved content before mounting the form, so a direct page load never edits (or saves) defaults.
export default function AdminContent() {
  const { loading } = useContent();
  if (loading) return <p className="text-sm text-muted-foreground">Loading content…</p>;
  return <AdminContentForm />;
}

function AdminContentForm() {
  const { toast } = useToast();
  const { content, refresh } = useContent();
  const [data, setData] = useState(() => JSON.parse(JSON.stringify(content)));
  const [tab, setTab] = useState("company");
  const [saving, setSaving] = useState(false);
  // Snapshot of what was loaded/last saved, so Save only sends sections that changed
  // (a stale admin tab must not overwrite edits made elsewhere).
  const baseline = useRef(JSON.stringify(content));

  const set = (path, value) => {
    setData((prev) => {
      const next = { ...prev };
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = Array.isArray(obj[keys[i]]) ? [...obj[keys[i]]] : { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  // Hero slider helpers
  const setHeroSlides = (slides) => set("hero.slides", slides);
  const setHeroSlide = (i, patch) =>
    setHeroSlides((data.hero?.slides || []).map((sl, idx) => (idx === i ? { ...sl, ...patch } : sl)));
  const removeHeroSlide = (i) => setHeroSlides((data.hero?.slides || []).filter((_, idx) => idx !== i));
  const moveHeroSlide = (i, dir) => {
    const arr = [...(data.hero?.slides || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setHeroSlides(arr);
  };

  const setArrItem = (key, idx, field, value) => {
    setData((prev) => {
      const arr = [...(prev[key] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, [key]: arr };
    });
  };
  const addArrItem = (key, template) => setData((prev) => ({ ...prev, [key]: [...(prev[key] || []), template] }));
  const removeArrItem = (key, idx) => setData((prev) => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));

  const setStrItem = (key, idx, value) => {
    setData((prev) => {
      const arr = [...(prev[key] || [])];
      arr[idx] = value;
      return { ...prev, [key]: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const base = JSON.parse(baseline.current);
      const changed = Object.fromEntries(
        Object.entries(data).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(base[k]))
      );
      if (Object.keys(changed).length) await adminApi.put("/admin/content", changed);
      baseline.current = JSON.stringify(data);
      toast({ title: "Content saved", description: "Your changes are live on the website." });
      refresh();
    } catch (err) {
      toast({ title: "Save failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-[#0f1e33]">Site Content</h1>
          <p className="text-[#5a6b7e] mt-1">Edit content shown across the public website.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#0e7490] hover:bg-[#0b5563] text-white h-11">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3.5 h-9 rounded-full text-sm font-medium border transition-colors ${tab === t.key ? "bg-[#0e7490] text-white border-[#0e7490]" : "bg-white text-[#3b4b60] border-border hover:border-[#0e7490]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-border p-6 lg:p-8 max-w-3xl">
        {tab === "company" && (
          <div className="space-y-4">
            <Field label="Name" value={data.company?.name} onChange={(v) => set("company.name", v)} />
            <Field label="Legal name" value={data.company?.legalName} onChange={(v) => set("company.legalName", v)} />
            <Field label="Tagline" value={data.company?.tagline} onChange={(v) => set("company.tagline", v)} />
            <Field label="Intro" textarea value={data.company?.intro} onChange={(v) => set("company.intro", v)} />
            <Field label="Email" value={data.company?.email} onChange={(v) => set("company.email", v)} />
            <Field label="Phone (display)" value={data.company?.phone} onChange={(v) => set("company.phone", v)} />
            <Field label="Phone (dial href, e.g. +9198...)" value={data.company?.phoneHref} onChange={(v) => set("company.phoneHref", v)} />
          </div>
        )}

        {tab === "hero" && (
          <div className="space-y-5">
            <p className="text-xs text-muted-foreground">The home page headline is the Tagline — edit it under Company → Tagline.</p>
            <Field label="Highlighted phrase (optional — must appear exactly in the Tagline)" value={data.hero?.titleHighlight} onChange={(v) => set("hero.titleHighlight", v)} />
            <hr className="border-border" />
            <div>
              <p className="font-medium text-[#0f1e33]">Hero slider images</p>
              <p className="text-xs text-muted-foreground mt-1">
                These rotate behind the home page headline. Visitors can also use the arrows, thumbnails or swipe. Each slide shows the recommended size below its upload button.
              </p>
            </div>
            <div className="w-48"><Field label="Seconds per slide" value={String(data.hero?.interval ?? 5)} onChange={(v) => set("hero.interval", v.replace(/[^0-9]/g, ""))} /></div>
            <div className="space-y-4">
              {(data.hero?.slides || []).map((sl, i) => (
                <div key={i} className="rounded-xl border border-border p-4 space-y-3 relative">
                  <p className="text-xs font-medium text-muted-foreground">Slide {i + 1}</p>
                  <ImageUpload label="" value={sl.image} onChange={(url) => setHeroSlide(i, { image: url })} hint={IMAGE_SPECS.heroSlide.hint} minWidth={IMAGE_SPECS.heroSlide.minW} minHeight={IMAGE_SPECS.heroSlide.minH} />
                  <Field label="Description (for accessibility)" value={sl.alt} onChange={(v) => setHeroSlide(i, { alt: v })} />
                  <div className="absolute top-3 right-3 flex items-center gap-1.5">
                    <button type="button" disabled={i === 0} onClick={() => moveHeroSlide(i, -1)} aria-label="Move up" className="h-8 w-8 rounded-lg border border-border flex items-center justify-center disabled:opacity-30 hover:bg-secondary"><ArrowUp className="h-4 w-4" /></button>
                    <button type="button" disabled={i === (data.hero?.slides || []).length - 1} onClick={() => moveHeroSlide(i, 1)} aria-label="Move down" className="h-8 w-8 rounded-lg border border-border flex items-center justify-center disabled:opacity-30 hover:bg-secondary"><ArrowDown className="h-4 w-4" /></button>
                    <button type="button" onClick={() => removeHeroSlide(i)} aria-label="Remove slide" className="h-8 w-8 rounded-lg border border-border flex items-center justify-center text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={() => setHeroSlides([...(data.hero?.slides || []), { image: "", alt: "" }])} disabled={(data.hero?.slides || []).length >= 8}>
                <Plus className="h-4 w-4 mr-1.5" /> Add slide
              </Button>
            </div>
            <hr className="border-border" />
            <div>
              <p className="font-medium text-[#0f1e33]">Section images</p>
              <p className="text-xs text-muted-foreground mt-1">{IMAGE_GENERAL_TIP}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {SECTION_IMAGE_SLOTS.map((slot) => (
                <div key={slot.key} className="rounded-xl border border-border p-4">
                  <p className="text-xs text-muted-foreground mb-2">Appears on: {slot.where}</p>
                  <ImageUpload
                    label={slot.label}
                    value={data.images?.[slot.key]}
                    onChange={(url) => set(`images.${slot.key}`, url)}
                    hint={slot.hint}
                    minWidth={slot.minW}
                    minHeight={slot.minH}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "stats" && (
          <div className="space-y-3">
            {(data.stats || []).map((s, i) => (
              <div key={i} className="flex items-end gap-3">
                <div className="w-28"><Field label="Value" value={s.value} onChange={(v) => setArrItem("stats", i, "value", v)} /></div>
                <div className="flex-1"><Field label="Label" value={s.label} onChange={(v) => setArrItem("stats", i, "label", v)} /></div>
                <button onClick={() => removeArrItem("stats", i)} className="h-10 w-10 rounded-lg border border-border flex items-center justify-center text-red-500 hover:bg-red-50 mb-0.5"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="outline" onClick={() => addArrItem("stats", { value: "", label: "" })}><Plus className="h-4 w-4 mr-1.5" /> Add stat</Button>
          </div>
        )}

        {(tab === "services" || tab === "whyChoose") && (
          <div className="space-y-4">
            <p className="text-xs text-[#9aa8b5]">Icon names use lucide-react (e.g. Factory, ShieldCheck, Truck, Network, Timer, Radar).</p>
            {(data[tab] || []).map((item, i) => (
              <div key={i} className="rounded-xl border border-border p-4 space-y-3 relative">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Icon" value={item.icon} onChange={(v) => setArrItem(tab, i, "icon", v)} />
                  <Field label="Title" value={item.title} onChange={(v) => setArrItem(tab, i, "title", v)} />
                </div>
                <Field label="Description" textarea value={item.desc} onChange={(v) => setArrItem(tab, i, "desc", v)} />
                <button onClick={() => removeArrItem(tab, i)} className="absolute top-3 right-3 h-8 w-8 rounded-lg border border-border flex items-center justify-center text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="outline" onClick={() => addArrItem(tab, { icon: "ShieldCheck", title: "", desc: "" })}><Plus className="h-4 w-4 mr-1.5" /> Add item</Button>
          </div>
        )}

        {tab === "processSteps" && (
          <div className="space-y-4">
            {(data.processSteps || []).map((item, i) => (
              <div key={i} className="rounded-xl border border-border p-4 space-y-3 relative">
                <div className="grid sm:grid-cols-3 gap-3">
                  <Field label="Number" value={item.n} onChange={(v) => setArrItem("processSteps", i, "n", v)} />
                  <div className="sm:col-span-2"><Field label="Title" value={item.title} onChange={(v) => setArrItem("processSteps", i, "title", v)} /></div>
                </div>
                <Field label="Description" textarea value={item.desc} onChange={(v) => setArrItem("processSteps", i, "desc", v)} />
                <button onClick={() => removeArrItem("processSteps", i)} className="absolute top-3 right-3 h-8 w-8 rounded-lg border border-border flex items-center justify-center text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="outline" onClick={() => addArrItem("processSteps", { n: "", title: "", desc: "" })}><Plus className="h-4 w-4 mr-1.5" /> Add step</Button>
          </div>
        )}

        {(tab === "certifications" || tab === "regions") && (
          <div className="space-y-3">
            {(data[tab] || []).map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <Input value={s} onChange={(e) => setStrItem(tab, i, e.target.value)} />
                <button onClick={() => removeArrItem(tab, i)} className="h-10 w-10 rounded-lg border border-border flex items-center justify-center text-red-500 hover:bg-red-50 shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="outline" onClick={() => setData((prev) => ({ ...prev, [tab]: [...(prev[tab] || []), ""] }))}><Plus className="h-4 w-4 mr-1.5" /> Add {tab === "regions" ? "region" : "certification"}</Button>
          </div>
        )}

        {tab === "testimonials" && (
          <div className="space-y-4">
            {(data.testimonials || []).map((item, i) => (
              <div key={i} className="rounded-xl border border-border p-4 space-y-3 relative">
                <Field label="Quote" textarea value={item.quote} onChange={(v) => setArrItem("testimonials", i, "quote", v)} />
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Name" value={item.name} onChange={(v) => setArrItem("testimonials", i, "name", v)} />
                  <Field label="Role" value={item.role} onChange={(v) => setArrItem("testimonials", i, "role", v)} />
                </div>
                <button onClick={() => removeArrItem("testimonials", i)} className="absolute top-3 right-3 h-8 w-8 rounded-lg border border-border flex items-center justify-center text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="outline" onClick={() => addArrItem("testimonials", { quote: "", name: "", role: "" })}><Plus className="h-4 w-4 mr-1.5" /> Add testimonial</Button>
          </div>
        )}
      </div>
    </div>
  );
}
